import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};

const isValidityExpired = (validityDate) => {
    if (!validityDate) return false;
    const vd = new Date(validityDate);
    return vd < new Date();
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
    }, []);

    const fetchUser = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/auth/me`);
            const userData = response.data;
            // Check validity on client side too
            if (userData.role === 'student' && isValidityExpired(userData.validity_date)) {
                logout();
                return;
            }
            setUser(userData);
        } catch (error) {
            if (error.response?.status === 403 && error.response?.data?.detail === 'VALIDITY_EXPIRED') {
                logout();
            } else {
                logout();
            }
        } finally {
            setLoading(false);
        }
    }, [logout]);

    useEffect(() => {
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            fetchUser();
        } else {
            setLoading(false);
        }
    }, [token, fetchUser]);

    // Periodic validity check (every 60 seconds)
    useEffect(() => {
        if (!user || user.role !== 'student') return;
        const interval = setInterval(() => {
            if (isValidityExpired(user.validity_date)) {
                logout();
            }
        }, 60000);
        return () => clearInterval(interval);
    }, [user, logout]);

    const login = async (email, password) => {
        try {
            const response = await axios.post(`${API}/auth/login`, { email, password });
            const { token: newToken, user: userData } = response.data;
            localStorage.setItem('token', newToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            setToken(newToken);
            setUser(userData);
            return { success: true };
        } catch (error) {
            const detail = error.response?.data?.detail || 'Login failed';
            const msg = detail === 'VALIDITY_EXPIRED'
                ? 'আপনার subscription মেয়াদ শেষ হয়েছে। যোগাযোগ করুন: info@bakkoshilpi.com'
                : detail === 'EMAIL_NOT_VERIFIED'
                ? 'EMAIL_NOT_VERIFIED'
                : detail;
            return { success: false, error: msg };
        }
    };

    const register = async (email, password, name, role = 'student', mobile_number = '') => {
        try {
            const response = await axios.post(`${API}/auth/register`, { email, password, name, role, mobile_number });
            if (response.data.requires_otp) {
                return { success: false, requires_otp: true, email: response.data.email, message: response.data.message };
            }
            const { token: newToken, user: userData } = response.data;
            localStorage.setItem('token', newToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            setToken(newToken);
            setUser(userData);
            return { success: true };
        } catch (error) {
            return { success: false, error: error.response?.data?.detail || 'Registration failed' };
        }
    };

    const verifyOtp = async (email, otp) => {
        try {
            const response = await axios.post(`${API}/auth/verify-otp`, { email, otp });
            const { token: newToken, user: userData } = response.data;
            localStorage.setItem('token', newToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            setToken(newToken);
            setUser(userData);
            return { success: true };
        } catch (error) {
            return { success: false, error: error.response?.data?.detail || 'OTP verification failed' };
        }
    };

    const value = { user, token, loading, login, register, verifyOtp, logout, isAuthenticated: !!token && !!user, fetchUser };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
